// ui/presenters/fundingSourceRowPresenter.js
import { i18n } from 'boot/i18n';

import { formatCalendarDate } from 'src/utils/DateUtils';

import { toAsOfDate, toDateStr } from '../../domain/services/effectiveDating';

/**
 * Presenter: turns a resolved FundingSource into the row the settings table renders.
 *
 * In `presenters/` because it holds no state and no reactivity — a pure mapping from a domain entity
 * to a view model, the Presenter role in the interface-adapters ring.
 *
 * IT EXISTS BECAUSE THE ROWS STOPPED BEING DOCUMENTS. The table used to bind its columns straight to
 * stored field names and format them in `format:` callbacks, which worked while one document was one
 * funder. Now a document is one VERSION of a funder, the rows arrive resolved and mapped, and the
 * formatting had to move somewhere testable rather than be rewritten against new field names inside
 * column definitions.
 *
 * IT IS TOLD THE DATE rather than reading the clock, like every other rule in this context. The
 * status column has three cases, not two, and the third one only exists relative to a date: a payer
 * whose earliest version starts later is neither active nor inactive today, it is scheduled. The row
 * arrives from `loadConfiguredFundingSources`, which resolves against the same `asOf`, so labeling it
 * "Active" would claim a payer applies today when the record says it does not — and the retirement
 * label below would be flatly wrong about a scheduled version that happens to be inactive.
 *
 * TWO IDS, AND CONFUSING THEM IS THE BUG THIS SHAPE PREVENTS. `id` is the version document, which the
 * table needs as its row key. `fundingSourceId` is the payer, and it is what every action takes —
 * editing, retiring, and the server-side usage check, which resolves authorizations that reference the
 * payer and would find none if handed a version id.
 */

/**
 * Whether this row's version has not taken effect yet.
 *
 * Both sides are read rather than compared raw: the stored date has to be a calendar date for the
 * comparison to mean anything, and an unreadable `asOf` cannot make a payer scheduled — it can only
 * make the answer unknowable, and the honest answer then is the payer's stored status. The read that
 * produced the row already refuses a date it cannot resolve against, so this is a guard rather than an
 * ordinary state.
 */
function isNotYetInForce(fundingSource, asOf) {
  const asOfDate = toAsOfDate(asOf);
  const effectiveFrom = toDateStr(fundingSource.effectiveFrom);

  return asOfDate !== null && effectiveFrom !== null && effectiveFrom > asOfDate;
} // isNotYetInForce()

function statusLabel(fundingSource, isScheduled) {
  if (isScheduled) {
    return i18n.t('settings.fundingSources.versions.startsOn', {
      date: formatCalendarDate(fundingSource.effectiveFrom),
    });
  }

  if (fundingSource.isActive) {
    return i18n.t('fundingSourceStatuses.active');
  }

  const retiredOn = formatCalendarDate(fundingSource.effectiveFrom);

  return retiredOn === null
    ? i18n.t('fundingSourceStatuses.inactive')
    : i18n.t('settings.fundingSources.versions.retiredOn', { date: retiredOn });
} // statusLabel()

/**
 * @param {import('../../domain/model/FundingSource.types').FundingSource} fundingSource
 * @param {string} asOf - 'YYYY-MM-DD'; the date the row is read as of, which is what decides whether
 *   this payer has started yet
 * @returns {Object}
 */
// eslint-disable-next-line import/prefer-default-export
export function presentFundingSourceRow(fundingSource, asOf) {
  const isScheduled = isNotYetInForce(fundingSource, asOf);

  return {
    // The table's row key, and the one place a version coordinate legitimately leaves this context.
    // Named for what it is so nobody mistakes it for the payer: it changes when a new version is
    // saved, which is what makes the table re-render the row rather than reuse a stale one.
    versionId: fundingSource.versionId,
    // What every row action acts on. See the note above.
    fundingSourceId: fundingSource.fundingSourceId,

    name: fundingSource.name,
    notes: fundingSource.notes,

    // A funder migrated from the legacy `other` or `regionalCenter` has no archetype yet, and reads
    // as blank rather than as a placeholder value — which is what it is.
    archetypeLabel: fundingSource.archetype === null
      ? ''
      : i18n.t(`fundingSourceArchetypes.${fundingSource.archetype}.label`),
    billingChannelLabel: fundingSource.billingChannel === null
      ? ''
      : i18n.t(`billingChannels.${fundingSource.billingChannel}.label`),

    servicesLabel: fundingSource.coveredServices.length > 0
      ? i18n.t('settings.fundingSources.numValidTreatmentServices', {
        count: fundingSource.coveredServices.length,
      })
      : i18n.t('settings.fundingSources.noValidTreatmentServices'),

    // The retirement DATE is the retiring version's own effective date, not a separate timestamp.
    // `inactiveTimestamp` used to carry it because retirement was a field written in place; retiring
    // is now a version, so the date it took effect is the only date there is — and unlike a stamp, it
    // cannot disagree with when the record actually changed.
    //
    // A funder retired before the backfill ran has no effective date to name, and the dated label
    // would render as "Inactive" followed by nothing. The undated status is the honest fallback.
    statusLabel: statusLabel(fundingSource, isScheduled),

    isActive: fundingSource.isActive,

    // The payer exists but applies from a later date. Retiring is "from today, inactive", which there
    // is nothing to say yet for a payer that has not started — so the row carries this rather than
    // leaving the page to re-derive it from a date it would have to compare itself.
    isScheduled,
  };
} // presentFundingSourceRow()
